<template>
  <div :class="rootClasses">
    <PageHeader
      v-if="!embedded"
      title="Live Products"
      description="Production products I built and helped ship."
      :breadcrumbs="[{ label: 'Home', to: '/' }, { label: 'Work' }]"
    />
    <SectionHeader
      v-else
      title="Live Products"
      description="Production products I built and helped ship."
    />

    <div v-if="products.length" class="space-y-8">
      <BaseCard
        v-for="product in products"
        :id="product.id"
        :key="product.id"
        hover
        :padding="false"
        class="overflow-hidden max-w-5xl scroll-mt-24"
      >
        <div class="flex flex-col">
          <a
            :href="product.liveUrl"
            target="_blank"
            rel="noopener noreferrer"
            :class="logoLinkClass(product)"
            :aria-label="`View ${product.name}`"
          >
            <img
              v-if="product.imageDark"
              :src="product.image"
              :alt="`${product.name} logo`"
              :class="logoImageClass(product)"
              class="dark:hidden"
            />
            <img
              v-if="product.imageDark"
              :src="product.imageDark"
              :alt="`${product.name} logo`"
              :class="logoImageClass(product)"
              class="hidden dark:block"
            />
            <img
              v-if="!product.imageDark"
              :src="product.image"
              :alt="`${product.name} logo`"
              :class="logoImageClass(product)"
            />
          </a>

          <div class="p-6 sm:p-8 min-w-0">
            <div class="flex flex-wrap items-center gap-2 mb-3">
              <BaseBadge variant="success">Live</BaseBadge>
              <p class="text-xs text-muted">{{ product.role }}</p>
            </div>

            <h3 class="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
              {{ product.name }}
            </h3>
            <p class="text-sm text-primary mt-1">{{ product.tagline }}</p>
            <p class="text-sm text-muted mt-4 leading-relaxed">{{ product.description }}</p>

            <div class="flex flex-wrap gap-2 mt-5">
              <BaseBadge v-for="tag in product.tags" :key="tag" variant="default" size="sm">
                {{ tag }}
              </BaseBadge>
            </div>

            <div class="mt-6">
              <BaseButton
                tag="a"
                variant="primary"
                :href="product.liveUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit Site
                <AppIcon name="ExternalLink" :size="16" />
              </BaseButton>
            </div>
          </div>
        </div>
      </BaseCard>
    </div>

    <BaseEmpty
      v-else
      icon="search"
      title="No live products yet"
      description="Featured products will appear here once they are published."
    />
  </div>
</template>

<script>
import { siteState } from '@/composables/useSiteData'
import PageHeader from '@/components/layout/PageHeader.vue'
import SectionHeader from '@/components/layout/SectionHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseBadge from '@/components/ui/BaseBadge.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseEmpty from '@/components/ui/BaseEmpty.vue'
import AppIcon from '@/components/ui/AppIcon.vue'

export default {
  name: 'WorkPage',
  components: {
    PageHeader,
    SectionHeader,
    BaseCard,
    BaseBadge,
    BaseButton,
    BaseEmpty,
    AppIcon,
  },
  props: {
    embedded: Boolean,
  },
  computed: {
    rootClasses() {
      return this.embedded
        ? 'page-container py-16 lg:py-20 animate-fade-in'
        : 'page-container animate-fade-in'
    },
    products() {
      return Array.isArray(siteState.liveProducts) ? siteState.liveProducts : []
    },
  },
  methods: {
    isBanner(product) {
      return product.logoLayout === 'banner'
    },
    logoLinkClass(product) {
      return this.isBanner(product)
        ? 'block h-36 sm:h-40 overflow-hidden bg-[#0b4c4e]'
        : 'flex items-center px-6 sm:px-8 py-6 border-b border-white/30 dark:border-white/10'
    },
    logoImageClass(product) {
      return this.isBanner(product)
        ? 'h-full w-full object-cover object-center'
        : 'h-11 sm:h-14 w-auto max-w-[min(100%,20rem)] object-contain object-left'
    },
  },
}
</script>
